/**
 * Formateadores `Intl` cacheados por idioma + opciones.
 *
 * Crear un `Intl.DateTimeFormat` es caro (resuelve el locale y compila el patrón) y
 * hacerlo en cada render se notaba en listas largas (react-doctor `js-hoist-intl`, 29
 * sitios, oct-2026). No se puede sacar a nivel de módulo porque casi todos dependen del
 * idioma activo (`i18n.language`), así que se cachea por (locale, opciones): misma
 * entrada → misma instancia. Úsalo siempre en lugar de `new Intl.DateTimeFormat(...)`.
 */
const dateTimeFormats = new Map<string, Intl.DateTimeFormat>()

/**
 * Zona horaria por defecto cuando las opciones no traen `timeZone`. En el navegador no se
 * fija (= la del usuario). La fija la Pages Function (`Europe/Madrid`): el Worker corre en
 * UTC y una partida a las 23:30 de Madrid salía con otra hora (o día) en el HTML del borde.
 */
let defaultTimeZone: string | undefined

export function setDefaultTimeZone(timeZone: string | undefined): void {
  defaultTimeZone = timeZone
}

export function dateTimeFormat(
  locale?: string | string[],
  options?: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  if (defaultTimeZone && !options?.timeZone) options = { ...options, timeZone: defaultTimeZone }
  const key = JSON.stringify([locale ?? null, options ?? null])
  let fmt = dateTimeFormats.get(key)
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(locale, options)
    dateTimeFormats.set(key, fmt)
  }
  return fmt
}

const relativeTimeFormats = new Map<string, Intl.RelativeTimeFormat>()

/** Igual que {@link dateTimeFormat} pero para `Intl.RelativeTimeFormat` ("hace 5 min"). */
export function relativeTimeFormat(
  locale?: string | string[],
  options?: Intl.RelativeTimeFormatOptions,
): Intl.RelativeTimeFormat {
  const key = JSON.stringify([locale ?? null, options ?? null])
  let fmt = relativeTimeFormats.get(key)
  if (!fmt) {
    fmt = new Intl.RelativeTimeFormat(locale, options)
    relativeTimeFormats.set(key, fmt)
  }
  return fmt
}
